import { Form, Input, Select } from 'antd';
import { useState, useCallback, useEffect, } from 'react';
import { apiClient, apiErrorMessage, toLowerTr } from '../../utils';
import { ConfirmGroupButton } from '../../elements/confim-group-button';
import { ErrorFinder, Equipment, Country, City, Division, Section, Cell, Supplier, FourM, User } from '../../models/index';

interface IQaSubErrorEditPageProps {
  errorId?: string;
  subErrorNo?: string;
  onSaved: (error?: Error) => void;
  onCancel: () => void
}

export function QaSubErrorEditPage(props: IQaSubErrorEditPageProps) {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [errorFinders, setErrorFinders] = useState<(ErrorFinder & { key: string })[]>();
  const [errorFindersLoading, setErrorFindersLoading] = useState(false);
  const [equipments, setEquipments] = useState<(Equipment & { key: string })[]>();
  const [equipmentsLoading, setEquipmentsLoading] = useState(false);
  const [countries, setCountries] = useState<(Country & { key: string })[]>();
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [cities, setCities] = useState<(City & { key: string })[]>();
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [divisions, setDivisions] = useState<(Division & { key: string })[]>();
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [sections, setSections] = useState<(Section & { key: string })[]>();
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [cells, setCells] = useState<(Cell & { key: string })[]>();
  const [cellsLoading, setCellsLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<(Supplier & { key: string })[]>();
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [fourMs, setFourMs] = useState<(FourM & { key: string })[]>();
  const [fourMsLoading, setFourMsLoading] = useState(false);
  const [users, setUsers] = useState<(User & { key: string })[]>();
  const [usersLoading, setUsersLoading] = useState(false);

  const loadErrorFinders = useCallback(() => {
    setErrorFindersLoading(true);
    apiClient
      .get(`error-finders`)
      .then((response: any) => {
        const responseErrorFinder: ErrorFinder[] = response.data;
        const responseData = responseErrorFinder.map(errorFinder => {
          return {
            key: errorFinder.id,
            ...errorFinder,
          };
        });
        setErrorFinders(responseData);
        setErrorFindersLoading(false);
      })
      .catch((reason: any) => {
        setErrorFindersLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadErrorFinders();
  }, [loadErrorFinders]);

  const loadEquipments = useCallback(() => {
    setEquipmentsLoading(true);
    apiClient
      .get(`equipments`)
      .then((response: any) => {
        const responseEquipment: Equipment[] = response.data;
        const responseData = responseEquipment.map(equipment => {
          return {
            key: equipment.id,
            ...equipment,
          };
        });
        setEquipments(responseData);
        setEquipmentsLoading(false);
      })
      .catch((reason: any) => {
        setEquipmentsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  const onChangeCountry = (id: string) => {
    setSelectedCountryId(id);
    form.setFieldsValue({ citiyId: "" });
  }

  const loadContries = useCallback(() => {
    setCountriesLoading(true);
    apiClient
      .get(`countries`)
      .then((response: any) => {
        const responseCountry: Country[] = response.data;
        const responseData = responseCountry.map(country => {
          return {
            key: country.id,
            ...country,
          };
        });
        setCountries(responseData);
        setCountriesLoading(false);
      })
      .catch((reason: any) => {
        setCountriesLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadContries();
  }, [loadContries]);


  const loadCities = () => {
    setCitiesLoading(true);
    apiClient
      .get(`cities/${selectedCountryId}`)
      .then((response: any) => {
        const responseCity: City[] = response.data;
        const responseData = responseCity.map(city => {
          return {
            key: city.id,
            ...city,
          };
        });
        setCities(responseData);
        setCitiesLoading(false);
      })
      .catch((reason: any) => {
        setCitiesLoading(false);
        apiErrorMessage(reason);
      });
  }

  useEffect(() => {
    if (!selectedCountryId) {
      return;
    }
    loadCities();
  }, [selectedCountryId]);

  const loadDivisions = useCallback(() => {
    setDivisionsLoading(true);
    apiClient
      .get(`production-sites/divisions`)
      .then((response: any) => {
        const responseDivision: Division[] = response.data;
        const responseData = responseDivision.map(division => {
          return {
            key: division.id,
            ...division,
          };
        });
        setDivisions(responseData);
        setDivisionsLoading(false);
      })
      .catch((reason: any) => {
        setDivisionsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadDivisions();
  }, [loadDivisions]);

  const loadSections = useCallback(() => {
    setSectionsLoading(true);
    apiClient
      .get(`production-sites/sections`)
      .then((response: any) => {
        const responseSection: Section[] = response.data;
        const responseData = responseSection.map(section => {
          return {
            key: section.id,
            ...section,
          };
        });
        setSections(responseData);
        setSectionsLoading(false);
      })
      .catch((reason: any) => {
        setSectionsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  const loadCells = useCallback(() => {
    setCellsLoading(true);
    apiClient
      .get(`production-sites/cells`)
      .then((response: any) => {
        const responseCell: Cell[] = response.data;
        const responseData = responseCell.map(cell => {
          return {
            key: cell.id,
            ...cell,
          };
        });
        setCells(responseData);
        setCellsLoading(false);
      })
      .catch((reason: any) => {
        setCellsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadCells();
  }, [loadCells]);

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    apiClient
      .get('users')
      .then(response => {
        const responseUser: User[] = response.data;
        const responseData = responseUser.map(user => {
          return {
            key: user.id,
            ...user,
          };
        });
        setUsers(responseData);
        setUsersLoading(false);
      })
      .catch((reason: any) => {
        setUsersLoading(false);
        apiErrorMessage(reason);
      })
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);


  const loadSuppliers = useCallback(() => {
    setSuppliersLoading(true);
    apiClient
      .get(`suppliers`)
      .then((response: any) => {
        const responseSupplier: Supplier[] = response.data;
        const responseData = responseSupplier.map(supplier => {
          return {
            key: supplier.id,
            ...supplier,
          };
        });
        setSuppliers(responseData);
        setSuppliersLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const loadFourMs = useCallback(() => {
    setFourMsLoading(true);
    apiClient
      .get(`four-ms`)
      .then((response: any) => {
        const responseFourM: FourM[] = response.data;
        const responseData = responseFourM.map(fourM => {
          return {
            key: fourM.id,
            ...fourM,
          };
        });
        setFourMs(responseData);
        setFourMsLoading(false);
      })
      .catch((reason: any) => {
        setFourMsLoading(false);
        apiErrorMessage(reason);
      });
  }, []);

  useEffect(() => {
    loadFourMs();
  }, [loadFourMs]);

  const onFinish = (values: any) => {
    setLoading(true);
    values.qaErrorId = props.errorId;
    if (!props.subErrorNo) {
      apiClient
        .post('qa-sub-errors', values)
        .then(() => {
          props.onSaved();
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      apiClient
        .put(`qa-sub-errors/${props?.errorId}`, values)
        .then((response: any) => {
          props.onSaved(response.data);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (

    <Form form={form} layout="vertical" name="basic" onFinish={onFinish} >
      {props.subErrorNo ?
        <Form.Item
          label="Hata no"
          name="subErrorNo"
        >
          <Input disabled={true} />
        </Form.Item>
        : null}

      <Form.Item
        label="Hatayı tespi eden"
        rules={[
          {
            required: true,
            message: 'Zorunlu alan',
          },
        ]}
        name="errorFinderId"
      >
        <Select
          loading={errorFindersLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {errorFinders?.map((e: ErrorFinder) => (
            <Select.Option key={e.id} value={e.id}>
              {e.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="İlk bulgu"
        name="firstFinding"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 200, message: 'En fazla 200 karakter' },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Malzeme"
        name="equipmentId"
        rules={[
          {
            required: true,
            message: 'Zorunlu alan',
          },
        ]}
      >
        <Select
          loading={equipmentsLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {equipments?.map((e: Equipment) => (
            <Select.Option key={e.id} value={e.id}>
              {e.code}({e.name})
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Ülke"
        name="countryId"
      >
        <Select
          loading={countriesLoading}
          onChange={onChangeCountry}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {countries?.map((c: Country) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Şehir"
        name="citiyId"
      >
        <Select
          loading={citiesLoading}
          disabled={citiesLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {cities?.map((c: City) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Bölüm"
        name="divisionId"
      >
        <Select
          loading={divisionsLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {divisions?.map((d: Division) => (
            <Select.Option key={d.id} value={d.id}>
              {d.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Kısım"
        name="sectionId"
      >
        <Select
          loading={sectionsLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {sections?.map((s: Section) => (
            <Select.Option key={s.id} value={s.id}>
              {s.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Hücre"
        name="cellId"
      >
        <Select
          loading={cellsLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {cells?.map((c: Cell) => (
            <Select.Option key={c.id} value={c.id}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Kişi"
        name="userId"
      >
        <Select
          disabled={usersLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {users?.map((u: User) => (
            <Select.Option key={u.id} value={u.id}>
              {u.fullName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Tedarikçi"
        name="supplierId"
      >
        <Select
          loading={suppliersLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {suppliers?.map((s: Supplier) => (
            <Select.Option key={s.id} value={s.id}>
              {s.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="4M"
        name="fourMId"
      >
        <Select
          loading={fourMsLoading}
          showSearch
          allowClear
          defaultValue={"Seçiniz"}
          filterOption={(input: any, option: any) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
          optionFilterProp="children">
          {fourMs?.map((f: FourM) => (
            <Select.Option key={f.id} value={f.id}>
              {f.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>


      <Form.Item>
        <ConfirmGroupButton onCancel={props.onCancel} loading={loading} />
      </Form.Item>
    </Form>
  );
}
